import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, Pipe, PipeTransform, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { KfbsFullCalendarComponent } from 'kfbs-nglib';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import { SaveChangesModalComponent } from 'src/app/core/modals/save-changes/save-changes-modal.component';
import { ComponentCanDeactivate } from 'src/app/core/services/can-deactivate-guard';
import { DeactivateGuardService } from 'src/app/core/services/deactivate-guard.service';
import { LogCategory, LoggerService, LogPage, LogType } from 'src/app/core/services/logger.service';
import { ProfileService } from 'src/app/core/services/profile.service';
import { SidebarService } from 'src/app/core/services/sidebar.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { Auction, BiddingCalendar, BiddingService, BidUpdate, IBid, IBidPoints } from 'src/app/services/bidding.service';
import { CountdownService, IInterval } from 'src/app/services/countdown.service';
import { CourseCatalogService } from 'src/app/services/course-catalog.service';
import { EnrollmentsService } from 'src/app/services/enrollments.service';
import { DisplayDetailsModalComponent } from '../../core/modals/display-details/display-details.component';
import { ErrorModalComponent } from '../../core/modals/error-modal/error-modal.component';
import { AuthenticationService } from '../../core/services/authentication.service';
import { TagManagerService } from '../../core/services/tag-manager.service';
import { ProfileDetailComponent } from '../people/person/profile-detail.component';
import { ProfileSetting } from '../profile/profile.component';

@Component({
    selector: 'course-catalog',
    templateUrl: './course-catalog.component.html',
    styleUrls: ['./course-catalog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CourseCatalogComponent extends ComponentCanDeactivate implements OnInit, AfterViewInit  {

    public selectedColumn: string = 'all';
    public isSearchRunning: boolean = false;
    public isFavoritesLoading: boolean = false;
    public isHistoryLoading: boolean = false;
    public isCollapsed: boolean = false;   
    public isActionable: boolean = false;
    public isUbpMod: boolean = false;
    public isVirtualClass: boolean = false;
    public isInPersonClass: boolean = false;
    public registration: boolean;
    public bidding: boolean = false;
    public isAuctionActive: boolean = false;
    public hasUpcoming: boolean = false;
    public currentAuction: Auction;
    
    public catalogSearch$: Observable<any>;
    public favoritesSearch$: Observable<any>;
    public actionable$: Observable<boolean>;
    public tab$: Observable<string>;
    public enrollmentHistorys$ = new BehaviorSubject([]);

    private pageItemCount: number;
    private userChangedFilter: boolean;
    public isDeleteBidRunning: any[] = [];    
    public isUpdateBidRunning: boolean;

    public allowCrossEnrollment: string[] = ["EMBA-E", "EMBA-W", "EMBA-EC"]; 

    private catalogSearchValues: any[] = [];
    private queryParams: object = {};

    //Program values
    @ViewChild('program') programElem: ElementRef;

    //Search form values
    @ViewChild('f') searchElem: NgForm;

    //Summary for scrollTo
    @ViewChild('searchSummary') summaryElem: ElementRef;

    //Bidding calendar
    @ViewChild('calendar') bidCalendar: KfbsFullCalendarComponent;

    public startTime: string;
    public endTime: string;    

    readonly STARTTIME =  "00:00 AM";
    readonly ENDTIME = "11:59 PM";

    public currentDate = this.utility.getCurrentDate().toISOString();

    //calendar settings
    public biddingCalendar;

    constructor(
        public auth: AuthenticationService,
        public catalog: CourseCatalogService,
        public enrollmentService: EnrollmentsService,
        public biddingService: BiddingService,
        public profileService: ProfileService,
        public sidebarService: SidebarService,
        private gtm: TagManagerService,
        private countdownService: CountdownService,
        private logger: LoggerService,
        private route: ActivatedRoute,        
        private modalService: NgbModal,
        public utility: UtilityService,
        private deactivateService: DeactivateGuardService,
        private changeDetector: ChangeDetectorRef
    ) {
        super();
        this.startTime = this.STARTTIME;
        this.endTime = this.ENDTIME;
        this.pageItemCount = 30;                    
        this.actionable$ = combineLatest(this.enrollmentService.hasActiveDropAdd$, this.biddingService.activeAuctions$)
            .pipe(
            switchMap(([rounds, auctions]) => {                           
                    this.registration = rounds.some(i => i.active == true);
                    this.bidding = auctions.some(i => i.bidding == true);
                    this.isAuctionActive = auctions.some(i => i.active == true);
                    this.hasUpcoming = rounds.some(i => i.upcoming);
                    if (this.isAuctionActive)
                        this.currentAuction = auctions.find(i => i.active == true);
                if (!this.userChangedFilter) {
                    this.isActionable = this.bidding || this.registration;

                    if (this.queryParams.hasOwnProperty('IsActionable'))
                        this.isActionable = this.queryParams['IsActionable'];
                }

                return of(this.bidding || this.registration || this.isAuctionActive)

            })
        );
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(method + ': ' + message, LogType.Page, LogPage.CourseCatalog, category);
    }
    
    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        if (this.catalog.updatedBids.length > 0) {
            $event.returnValue = true;
        }
    }

    canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
        if (this.catalog.updatedBids.length > 0) {
            this.modalService.open(SaveChangesModalComponent);
            return this.deactivateService.navigateAwaySelection$;
        }
        else
            return true;
    }

    ngOnInit(): void {      
        this.route.queryParamMap.
            subscribe(params => {
                var tabToShow = 'search';

                var tab = params.get('tab');
                switch (tab) {
                    case 'enrollmentHistory':
                        if (this.auth.hasFeature('EnrollmentHistory'))
                            tabToShow = tab;
                        break;
                    case 'waitlistManagement':
                        if (this.auth.hasFeature('WaitlistManagement'))
                            tabToShow = tab;
                        break;
                    default:
                        if (tab)
                            tabToShow = tab;
                        break;
                }

                this.catalog.tabToShow = tabToShow;

                if (params.keys.length > 0) {
                    if (params.get('program'))
                        this.queryParams['Program'] = params.get('program');

                    if (params.get('academicYear'))
                        this.queryParams['AcademicYear'] = params.get('academicYear');

                    if (params.get('courseTitle'))
                        this.queryParams['TitleLike'] = params.get('courseTitle');

                    if (params.get('instructorName'))
                        this.queryParams['InstructorNameLike'] = params.get('instructorName');

                    if (params.get('classType'))
                        this.queryParams['SectionType'] = params.get('classType');

                    if (params.get('classNum'))
                        this.queryParams['CourseNumberLike'] = params.get('classNum');

                    if (params.get('courseDesc'))
                        this.queryParams['DescriptionLike'] = params.get('courseDesc');

                    if (params.get('academicArea'))
                        this.queryParams['AcademicArea'] = params.get('academicArea');

                    if (params.get('isActionable'))
                        this.queryParams['IsActionable'] = params.get('isActionable').toLowerCase() == 'true' ? true : false;

                    if (params.get('sectionId'))
                        this.queryParams['SectionId'] = params.get('sectionId');

                    if (params.get('courseId'))
                        this.queryParams['CourseId'] = params.get('courseId');

                }
            })

        this.biddingCalendar = BiddingCalendar.getInstance();
        //check should we unsubscribe? calendarEvent$ is only triggered on a button click
        this.biddingCalendar.calendarEvent$
            .subscribe(
                (event) => {
                    if (this.bidCalendar)
                        this.bidCalendar.options.eventSources = event;
                })
        this.log("ngOnInit", null, LogCategory.Init);        
    }

    ngAfterViewInit() {       
        // See https://blog.angularindepth.com/everything-you-need-to-know-about-the-expressionchangedafterithasbeencheckederror-error-e3fd9ce7dbb4 
        this.changeDetector.detectChanges();

        //  We have to wait until after the view init has finished to get the bidding/registration responses
        //  or we can't logic the queryParams correctly
        if (Object.keys(this.queryParams).length > 0) {
            if (this.bidding || this.registration) {
                if (!this.queryParams.hasOwnProperty('IsActionable'))
                    this.queryParams['IsActionable'] = true;

                this.isActionable = this.queryParams['IsActionable'];
            }

            //  If it's not bidding or registration we shouldn't be able to toggle IsActionable whether it's passed
            //  through or not
            if (!this.bidding && !this.registration && this.queryParams['IsActionable'])
                delete this.queryParams['IsActionable'];

            //  We need the queryParams to keep the IsActionable in place for the UI toggle to represent correctly
            //  but we shouldn't pass IsActionable through to the API if it's false
            var searchFilters = Object.assign({}, this.queryParams);

            if(!this.isActionable)
                delete searchFilters['IsActionable'];

            this.isSearchRunning = true;
            this.catalogSearch$ = this.searchCatalog(1, 'all', searchFilters, false);
        }
    }

    public updateSavedYear(selectedYear, defaultYear) {
        this.queryParams['AcademicYear'] = selectedYear;

        if (+selectedYear >= +defaultYear) {
            var data: ProfileSetting[] = [new ProfileSetting()]
            data[0].Id = ".Myke.CatalogYear";
            data[0].Type = "text";
            data[0].Value = selectedYear;
            this.profileService.updateSettings(data, false);
        }
    }

    public onSubmit(f: NgForm) {
        this.isSearchRunning = true;

        var program = this.programElem.nativeElement.value;
        f.value.Program = program;

        //  We only store the last searched program for employees, students always reset to their primary program on page load
        if (this.profileService.profile.employeeData.IsEmployee)
            this.catalog.updateDefaultProgram(program);

        var daysArray = [];
        var termsArray = [];
        var blocksArray = [];
        for (let prop in f.value) {
            if (prop.indexOf('Day-') > -1) {
                if (f.value[prop])
                    daysArray.push(prop.substring(4));
            }
            if (prop.indexOf('Term-') > -1) {
                if (f.value[prop]) {
                    var values = prop.substring(5).split("#");                   
                    termsArray.push(values);
                }
            }
            if (prop.indexOf('Block') > -1) {
                if (f.value[prop]) {                    
                    blocksArray.push(prop);
                }
            }

            if (!f.value[prop]) {               
                delete f.value[prop];
            }            
        }
        
        var days = daysArray.join('');
        var noTimeslot = ((days == '') && (this.startTime == this.STARTTIME) && (this.endTime == this.ENDTIME));
        if(!noTimeslot)
            f.value.Timeslot = days + ' ' + this.startTime + ' - ' + this.endTime;
               
        if (termsArray.length > 0)
            f.value.TermIds = [].concat(...termsArray);

        if (blocksArray.length > 0)
            f.value.Blocks = blocksArray;
                              
        this.catalogSearchValues = f.value;

        if (this.isActionable && (this.registration || this.bidding ))
            f.value.IsActionable = true;
        else
            delete f.value.IsActionable;       

        if (this.isUbpMod && f.value.Program == 'UBP')
            f.value.IsUbpMod = true;
        else
            delete f.value.IsUbpMod;

        if (this.isVirtualClass && this.allowCrossEnrollment.includes( f.value.Program ))
            f.value.IsVirtualClass = true;
        else
            delete f.value.IsVirtualClass;
        
        this.catalogSearch$ = this.searchCatalog(1, 'all', this.catalogSearchValues, false);
        
    }

    public searchCatalog(pageNumber, selectedColumn, searchFilters, isFavorite): Observable<any> {
        //ga - log search click
        this.gtm.gaSearch('Catalog');
        this.isSearchRunning = true;
        this.isFavoritesLoading = true;
        return this.catalog.catalog$(pageNumber, this.pageItemCount, this.bidding, this.registration, searchFilters).pipe(
            tap((result) => {

                //  If we're searching from the CourseId query string, populate the minimum fields to find the same result again
                if (searchFilters.CourseId) {
                    var foundCourse = result.Courses[0];

                    this.searchElem.resetForm({
                        AcademicYear: foundCourse.AcademicYear.split("/")[0].trim(),
                        SectionType: foundCourse.CourseType.trim(),
                        CourseNumberLike: foundCourse.Number.trim(),
                        endTime: this.ENDTIME,
                        startTime: this.STARTTIME
                    });
                    //  Program can't be set directly like the other form elements, you have to set the dropdown value for this one
                    this.programElem.nativeElement.value = foundCourse.InferredProgram;
                }

                result.selectedColumn = selectedColumn;
                result.isFavorite = isFavorite;
                result.searchFilters = searchFilters;
            }),
            finalize(() => {                
                this.isSearchRunning = false;
                this.isFavoritesLoading = false;                               
            })
        );       
    }

    public goToTop() {        
        this.summaryElem.nativeElement.scrollIntoView({ behavior: "smooth" });
    }
  
    public onShowFavorites(favorites) {
        this.catalog.tabToShow = 'favorites';
        if (favorites.length > 0) {
            this.isFavoritesLoading = true;
            var sectionSearch = {
                SectionIds: favorites
            }
            this.favoritesSearch$ = this.searchCatalog(1, 'all', sectionSearch, true);
        } else {
            this.favoritesSearch$ = null;
        }
    }

    public onPaging(pageNumber, selectedColumn, searchFilters, isFavorite) {
        if (isFavorite)
            this.favoritesSearch$ = this.searchCatalog(pageNumber, selectedColumn, searchFilters, isFavorite);
        else
            this.catalogSearch$ = this.searchCatalog(pageNumber, selectedColumn, searchFilters, isFavorite);
    }

    public onPageItemsClick(pageItemCount) {
        if (this.pageItemCount != pageItemCount) {
            this.isSearchRunning = true;
            this.pageItemCount = pageItemCount;
            this.catalogSearch$ = this.searchCatalog(1, 'all', this.catalogSearchValues, false);
        }
    }
    
    public toggleActionable() {
        this.isActionable = !this.isActionable;
        this.userChangedFilter = true;
    }

    public toggleUbpMod() {
        this.isUbpMod = !this.isUbpMod;
    }

    public resetSearchFields(f: NgForm, defaults, isRegistrationOrBidding) {    
        this.catalogSearch$ = null;
        if (isRegistrationOrBidding)
            this.programElem.nativeElement.value = 'All';
        else if (this.catalog.defaultProgram == '')
            this.programElem.nativeElement.value = defaults.program
        else
            this.programElem.nativeElement.value = this.catalog.defaultProgram;

        f.resetForm({ AcademicYear: defaults.academicyear });        
        this.startTime = "00:00 AM";
        this.endTime = "11:59 PM";
        this.isUbpMod = false;
    }
        
    public openProfile(openedInstructor) {
        const modalRef = this.modalService.open(ProfileDetailComponent);
        modalRef.componentInstance.person = openedInstructor; 
        modalRef.componentInstance.searchterm = "";
    }

    public waitlistHelpExpand() {
        this.catalog.waitlistHelpExpand = !this.catalog.waitlistHelpExpand;
        this.catalog.updateWaitlistHelpExpand(this.catalog.waitlistHelpExpand);
    }

    public dismissWaitlistAlert(currentRounds) {
        this.catalog.updateDismissWaitlistAlert(currentRounds);
    }

    public changeCourseSorting(sorting) {

        if (this.catalog.sortCoursesByName != sorting) {
            this.isSearchRunning = true;            
            this.catalog.updateSortCoursesByName(sorting);
            this.catalogSearch$ = this.searchCatalog(1, 'all', this.catalogSearchValues, false);
        }
    }
    
    public FormatDate(stringDate) {
        var options = { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' } as const;
        return new Date(stringDate).toLocaleDateString('en-US', options);
    }

    public hasConflict(sectionId, conflicts) {
        return conflicts.filter( c => c.SectionId == sectionId).length > 0
    }

    public getConflictName(sectionId, conflicts, bids) {
        var conflict = conflicts.find(c => c.SectionId == sectionId);
        var retVal = "";
        if (conflict!='undefined') {
            retVal = bids.find(b => b.SectionId == conflict.ConflictingSectionId).ClassName;
        }

        return retVal;
    }

    public GetCurrentStatus(timeline) {        
        return timeline.find(t => t.CurrentState == true).Value;
    }

    public showAuctionActions(timeline) {
        var state = '';
        var currentState = timeline.find(t => t.CurrentState == true);
        if (currentState) {
            state = currentState.Value;
        }
        
        return ( state == 'Place your bids' || state == 'Fix your conflicts' )
    }

    public showEditableBid(timeline) {
        var state = '';
        var currentState = timeline.find(t => t.CurrentState == true);
        if (currentState) {
            state = currentState.Value;
        }

        return state == 'Place your bids';
    }

    public getCountDown$(states: any[]): Observable<IInterval> {

        var nextState = states.find(s => s.NextState);
        if (nextState) {
            var endDate = new Date(nextState.Key);
            return this.countdownService.CountDown$(endDate);
        }
        return null;
    }
        
    public bidUpdate(bid: IBid) {      
            if (this.catalog.updatedBids.indexOf(bid.SectionId) < 0)
                this.catalog.updatedBids.push(bid.SectionId);    
    }

    public deleteBid(auction: IBidPoints, bid: IBid, groupedBid, actionType) {
        this.isDeleteBidRunning[bid.SectionId] = true;
        if (this.currentAuction != null) {
            const modalRefConfirm = this.biddingService.openBidDeleteConfirmModal(bid, actionType);
            modalRefConfirm.result.then((result) => {
                if (result == 'ok') {
                    this.biddingService.delete(bid.SectionId, this.currentAuction.auctionId, true).subscribe((result) => {
                        this.biddingService.updateBiddingCalendar(this.biddingCalendar);
                        this.isDeleteBidRunning[bid.SectionId] = false;
                        const modalRef = this.modalService.open(DisplayDetailsModalComponent);

                        if (result['IsSuccess'])
                            modalRef.componentInstance.title = '<i class="far fa-check-circle text-success"></i><span class="ml-1"> ' + result['ActionRequested'] + '</span>';
                        else
                            modalRef.componentInstance.title = '<i class="fas fa-exclamation-circle text-red"></i><span class="ml-1"> Unable to ' + result['ActionRequested'] + '</span>';

                        modalRef.componentInstance.body = result['UserMessage'];
                        modalRef.componentInstance.okButton = "Got it!!";
                    })
                }
            }, () => {
                this.isDeleteBidRunning[bid.SectionId] = false;
            });           
        } else {
            this.isDeleteBidRunning[bid.SectionId] = false;
            this.modalService.open(ErrorModalComponent);
        }
    }

    public updateBids(auction: IBidPoints) {
        var bidsToUpdate = [];
        this.isUpdateBidRunning = true;
        auction.Bids.filter(b => this.catalog.updatedBids.indexOf(b.SectionId) > -1).forEach((bid) => {
            bidsToUpdate.push(new BidUpdate(this.currentAuction.auctionId, bid.SectionId, this.currentAuction.roundId, bid.Points));
        })

        this.biddingService.updateBids(bidsToUpdate).subscribe((result) => {
            this.catalog.updatedBids = [];
            this.isUpdateBidRunning = false;            
            this.enrollmentService.openEnrollmentResultModal(result);
        })
    }

    public hasKeywords(lookup, program: string[]) {
        return lookup.AppliesTo.some(l => program.includes(l));

    }
}

@Pipe({
    name: 'termsFilter',
    standalone: false
})
export class TermsFilter implements PipeTransform {
    transform(items: any, academicYear: string, defaultAcademicYear: string, program: string, classType: string): any {
        if (program == '') return [];

        var year = academicYear == undefined ? defaultAcademicYear : academicYear; 
        var key = year + '|' + program;

        var retVal = items.filter(term => term.Key == key).flatMap( v => v.Value);        
        
        if (classType != undefined && classType != '')
            retVal = retVal.filter(v => v.CourseType == classType);
       
        retVal.sort(function (a, b) {
            return a.Id - b.Id;
        });
       
        var terms = [];

        // build our own collection of key/value so we can keep the order
        // if we use the angular keyvalue pipe, it will automatically sort it by key
        retVal.forEach((element, index, array) => {
            var elem = terms.find(t => t.key == element.Name)
            if (elem != undefined) {               
                elem.value.push(element.Id);
            } else {
                terms.push({ 'key': element.Name, 'value': [element.Id] });
            }
        });
               
        return terms;
    }
}

@Pipe({
    name: 'sectionsStartsInTermFilter',
    standalone: false
})
export class SectionsStartsInTermFilter implements PipeTransform {
    transform(items: any[], termName: string ) {
        if (termName == '') return items;       
        return items.filter(s => s.StartsInTermName == termName);        
    }
}

@Pipe({
    name: 'groupByFilter',
    standalone: false
})

export class GroupByFilter implements PipeTransform {
    transform(items: IBid[], group: string) {
        if (!items) return [];
        
        const groupedCollection = items.reduce((previous, current) => {
            if (!previous[current[group]]) {
                previous[current[group]] = [current];
            } else {
                previous[current[group]].push(current);
            }

            return previous;
        }, {});
        
        // this will return an array of objects, each object containing a group of objects
        return Object.keys(groupedCollection).map(key => ({ key, value: groupedCollection[key] }));
    }
}


@Pipe({
    name: 'waitlistFilter',
    standalone: false
})

export class WaitlistFilterPipe implements PipeTransform {
    transform(items: any[]): any[] {
        if (!items) return [];

        return items.filter(item => {
            return item.Status == 'Waitlisted' && item.Conflicts.length > 0
        });

    }
}

@Pipe({
    name: 'conflictTermFilter',
    standalone: false
})

export class ConflictTermFilterPipe implements PipeTransform {
    transform(items: any[], enrollments: any): any[] {
        if (!items) return [];
        if (!enrollments) return [];

        return items.filter(it => {
            return this.isValidTerm(it.Ids, enrollments)
        });

    }

    private isValidTerm(ids: any[], enrollments: any): Boolean {
        var showTerm = false;
            ids.forEach(id => {
                for (let enrollment of enrollments) {
                    if (enrollment.Status == 'Waitlisted' && enrollment.Conflicts.length > 0) {
                        for (let term of enrollment.Section.Terms) {
                            if (term.TermId == id) 
                                showTerm = true;
                        }
                    }
                }
            });
            return showTerm;
    }
}

@Pipe({
    name: 'localTimeZone',
    standalone: false
})

export class LocalTimeZonePipe implements PipeTransform {    
    transform(dateValue: Date, type: string): string {
        var options = {};
        switch (type) {
            case "time": {
                options = { hour: 'numeric', minute: 'numeric', timeZoneName: 'short' };
                return new Date(dateValue).toLocaleTimeString('en-US', options);                
            }                            
            case "date": {
                options = { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' };
                return new Date(dateValue).toLocaleDateString('en-US', options);                
            }
            case "full-date": {
                options = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' };                
                return new Date(dateValue).toLocaleDateString('en-US', options);
            }
        }

        
    }

}


